import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  Typography,
  useTheme,
} from '@mui/material';
import type { ReactElement } from 'react';
import { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  useAuthStore,
  useLogout,
  useSubscription,
  useUserStore,
} from 'services/hooks';
import { locations } from 'services/routes/locations';
import { events } from 'services/analytics';
import useAnalytics from 'services/hooks/analytics';
import { affiliate } from 'services/routes/externalLocations/affiliate';
import useStyles from './style';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};
type ToggleEvent = KeyboardEvent | MouseEvent;

const MobileSideBarMenu = ({ isOpen, setIsOpen }: Props): ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;
  const { userStore } = useUserStore();
  const { track } = useAnalytics();
  const logout = useLogout();
  const { isSubscriptionActive } = useSubscription();

  const isKeyboardEvent = (event: ToggleEvent): event is KeyboardEvent =>
    (event as KeyboardEvent).getModifierState !== undefined;

  const toggleDrawer = (open: boolean) => (event: ToggleEvent) => {
    if (
      isKeyboardEvent(event) &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setIsOpen(open);
  };

  const handleLogout = () => {
    logout();
  };

  const handleAffiliateClick = () => {
    toggleDrawer(false);
    track(events.affiliateClicked());
  };

  return (
    <Drawer
      open={isOpen}
      variant="persistent"
      className={classes.drawer}
      classes={{
        paper: classes.drawerPaper,
      }}
      anchor="left"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          pr: 2,
        }}
      >
        <IconButton onClick={() => setIsOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 6 }}>
        <List className={classes.pageTextLinks}>
          <ListItem data-testid="ai-music-genenerator">
            <Typography
              variant="subtitle1"
              component={Link}
              to={locations.general.aiMusicGenerator.pathname}
            >
              {t('general:topBar:create')}
            </Typography>
          </ListItem>
          <ListItem data-testid="discover-playlists">
            <Typography
              variant="subtitle1"
              component={Link}
              to={locations.general.discovery.pathname}
            >
              {t('general:topBar:discoverPlaylists')}
            </Typography>
          </ListItem>
          <ListItem>
            <Typography
              variant="subtitle1"
              component={Link}
              to={locations.general.songList.pathname}
            >
              {t('general:topBar:templates')}
            </Typography>
          </ListItem>

          {isLoggedIn && userStore ? (
            <>
              <ListItem onClick={toggleDrawer(false)} data-testid="library">
                <Typography
                  variant="subtitle1"
                  component={Link}
                  to={locations.library.pathname}
                >
                  {t('general:topBar:library')}
                </Typography>
              </ListItem>
              <ListItem onClick={toggleDrawer(false)} data-testid="releases">
                <Typography
                  variant="subtitle1"
                  component={Link}
                  to={locations.releases.pathname}
                >
                  {t('general:topBar:releases')}
                </Typography>
              </ListItem>
              <ListItem
                onClick={handleAffiliateClick}
                data-testid="affiliate-button"
              >
                <Typography
                  variant="subtitle1"
                  component="a"
                  href={affiliate.url}
                >
                  {t(affiliate.label)}
                </Typography>
              </ListItem>
              <ListItem data-testid="user-info">
                <Typography
                  variant="subtitle1"
                  component={Link}
                  to={locations.account.main.pathname}
                >
                  {userStore.email && `${userStore.email}`}
                </Typography>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem onClick={toggleDrawer(false)} data-testid="releases">
                <Typography
                  variant="subtitle1"
                  component={Link}
                  to={locations.releases.pathname}
                >
                  {t('general:topBar:releases')}
                </Typography>
              </ListItem>
              <ListItem
                onClick={handleAffiliateClick}
                data-testid="affiliate-button"
              >
                <Typography
                  variant="subtitle1"
                  component="a"
                  href={affiliate.url}
                >
                  {t(affiliate.label)}
                </Typography>
              </ListItem>
              <ListItem
                onClick={toggleDrawer(false)}
                data-testid="sing-in-button"
              >
                <Typography
                  variant="subtitle1"
                  component={Link}
                  to={locations.general.login.pathname}
                >
                  {t('auth:logIn')}
                </Typography>
              </ListItem>
            </>
          )}
          {!isLoggedIn && (
            <ListItem
              onClick={toggleDrawer(false)}
              data-testid="pricing-button"
            >
              <Typography
                variant="subtitle1"
                component={Link}
                to={locations.subscription.pricing.pathname('')}
              >
                {t('general:topBar:pricing')}
              </Typography>
            </ListItem>
          )}
        </List>

        {isLoggedIn && (
          <Box>
            <Divider />
            <List className={classes.legalLinks}>
              <ListItem onClick={handleLogout} data-testid="logout-button">
                <Typography
                  component={Link}
                  to={locations.general.songList.pathname}
                  variant="body1"
                  onClick={toggleDrawer(false)}
                >
                  {t('account:sideBar:logOut')}
                </Typography>
              </ListItem>
            </List>
          </Box>
        )}
        {!isLoggedIn && (
          <Box>
            <Button
              component={Link}
              to={locations.general.register.pathname('')}
              data-testid="start-now-button"
              disableRipple
              color="primary"
              size="large"
              variant="rounded"
              fullWidth
              sx={{ mt: theme.spacing(3) }}
            >
              {t('general:topBar:startNow').toUpperCase()}
            </Button>
          </Box>
        )}
        {!isSubscriptionActive && isLoggedIn && (
          <Box>
            <Button
              component={Link}
              to={locations.subscription.pricing.pathname('')}
              data-testid="start-now-button"
              disableRipple
              color="primary"
              size="large"
              variant="rounded"
              fullWidth
              sx={{ mt: theme.spacing(3) }}
            >
              {t('general:topBar:upgrade').toUpperCase()}
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default MobileSideBarMenu;
